/**
 * Shipment Detail Drawer
 * Tabbed interface with Overview and Order tabs
 * Uses BaseDrawer for consistent sizing
 */

import { useState, useEffect, useRef } from 'react'
import { useTranslation, useIsRTL } from '@spcc/core'
import {
  Badge,
  Button,
  cn,
  BaseDrawer,
} from '@spcc/uikit'
import {
  Truck,
  MapPin,
  User,
  CalendarDays,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Gauge,
  ArrowDown,
  Phone,
  ShoppingCart,
  Loader2,
  Layers,
} from '@spcc/uikit'
import type { Shipment, ShipmentStatus, ShipmentWeighbridge } from '../../domain/types'
import { ordersApi, OrderCard, type Order } from '@spcc/domains/orders'
import { shipmentsApi } from '../../infrastructure/api/shipments-api'
import { transformWeighbridgeTickets } from '../../api/transformers'

interface ShipmentDetailDrawerProps {
  shipment: Shipment | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Callback when clicking the order to open its drawer */
  onViewOrder?: (orderId: string) => void
  /** Callback to open Sales Order drawer */
  onViewSalesOrder?: (salesOrderId: string) => void
}

const statusConfig: Record<ShipmentStatus, { icon: typeof Clock; gradient: string; bg: string; text: string }> = {
  scheduled: { icon: CalendarDays, gradient: 'from-slate-500 to-slate-600', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400' },
  loading: { icon: Package, gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  delivered: { icon: CheckCircle, gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  cancelled: { icon: XCircle, gradient: 'from-red-500 to-red-600', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
}


export function ShipmentDetailDrawer({ shipment, open, onOpenChange, onViewOrder }: ShipmentDetailDrawerProps) {
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const isArabic = i18n.language === 'ar'
  const [activeTab, setActiveTab] = useState('overview')
  const [order, setOrder] = useState<Order | null>(null)
  const [orderLoading, setOrderLoading] = useState(false)
  const [weighbridge, setWeighbridge] = useState<ShipmentWeighbridge | undefined>(undefined)
  const [weighbridgeLoading, setWeighbridgeLoading] = useState(false)
  const weighbridgeLoadedRef = useRef(false)

  // Load order when order tab is activated
  useEffect(() => {
    if (activeTab === 'order' && shipment && !order) {
      setOrderLoading(true)
      ordersApi.getOrder(shipment.orderId).then((data) => {
        setOrder(data)
        setOrderLoading(false)
      }).catch(() => {
        setOrderLoading(false)
      })
    }
  }, [activeTab, shipment, order])

  // Load weighbridge tickets when weighbridge tab is activated
  useEffect(() => {
    if (activeTab === 'weighbridge' && shipment) {
      // First check if we have weighbridge data in metadata
      const metadata = (shipment as any).metadata
      if (metadata?.readings && Array.isArray(metadata.readings)) {
        const grossReading = metadata.readings.find((r: any) => r.stage === 'gross')
        const tareReading = metadata.readings.find((r: any) => r.stage === 'tare')
        
        const gross = grossReading?.grossWeight
        const tare = tareReading?.tareWeight
        let net = undefined
        
        if (typeof gross === 'number' && typeof tare === 'number') {
          net = gross - tare
        }
        
        if (gross !== undefined || tare !== undefined) {
           setWeighbridge({
             grossWeight: gross,
             tareWeight: tare,
             netWeight: net,
             ticketNumber: metadata.ticketNumber || '', // Optional fallback
             measuredAt: grossReading?.measuredAt || tareReading?.measuredAt || new Date().toISOString(),
             scaleId: grossReading?.scaleId || '',
             // Add other fields if required by ShipmentWeighbridge type
             tickets: [] 
           } as any) // Type casting to satisfy the interface if needed, or rely on loose typing
           return
        }
      }

      // Fallback to API if not in metadata or incomplete
      if (!weighbridgeLoadedRef.current) {
        weighbridgeLoadedRef.current = true
        setWeighbridgeLoading(true)
        shipmentsApi.getShipmentWeighbridgeTickets(shipment).then((tickets) => {
          const weighbridgeData = transformWeighbridgeTickets(tickets)
          setWeighbridge(weighbridgeData)
          setWeighbridgeLoading(false)
        }).catch(() => {
          setWeighbridgeLoading(false)
        })
      }
    }
  }, [activeTab, shipment?.id])

  // Reset state when drawer closes or shipment changes
  useEffect(() => {
    if (!open) {
      setActiveTab('overview')
      setOrder(null)
      setWeighbridge(undefined)
      setWeighbridgeLoading(false)
      weighbridgeLoadedRef.current = false
    }
  }, [open, shipment?.id])

  if (!shipment) return null

  const config = statusConfig[shipment.status]
  const StatusIcon = config.icon
  // weighbridge is loaded from state (lazy loaded when tab is activated)

  const formatWeighbridgeWeight = (value?: number | null) => {
    if (value == null || !Number.isFinite(value)) return t('common.notAvailable')
    const unit = value > 200 ? 'kg' : 'tons'
    const formatted = value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    const unitLabel = unit === 'kg' ? 'kg' : t('products.tons')
    return `${formatted} ${unitLabel}`
  }

  const formatWeighbridgeDate = (value?: string | null) => {
    if (!value) return t('common.notAvailable')
    const trimmed = value.trim()
    if (!trimmed) return t('common.notAvailable')
    return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed
  }

  const formatWeighbridgeText = (value?: string | null) => {
    if (!value) return t('common.notAvailable')
    const trimmed = value.trim()
    return trimmed ? trimmed : t('common.notAvailable')
  }

  // Header content for BaseDrawer
  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <StatusIcon className="w-6 h-6 text-white" />
      </div>
      <div>
        <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm mb-1">
          {t(`shipments.${shipment.status}`)}
        </Badge>
        <h2 className="text-lg font-bold text-white">{shipment.shipmentNumber}</h2>
      </div>
    </div>
  )

  // Top bar content
  const topBarContent = (
    <div className="flex gap-8">
      <div className="flex items-center">
        <span className="text-sm text-slate-500 dark:text-slate-400">{t('shipments.order')}</span>
        <span className="ms-2 text-sm font-mono font-semibold text-primary">{shipment.orderNumber}</span>
      </div>
      <div className="flex items-center">
        <span className="text-sm text-slate-500 dark:text-slate-400">{t('shipments.quantity')}</span>
        <span className="ms-2 text-sm font-semibold text-foreground">{shipment.quantity.value} {shipment.quantity.unit}</span>
      </div>
    </div>
  )

  // Footer content
  const footerContent = (
    <div className="flex gap-3">
      <Button variant="outline" className="flex-1 h-12 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700" onClick={() => onOpenChange(false)}>
        {t('common.close')}
      </Button>
    </div>
  )

  // Tabs content for stickyContent
  const tabsContent = (
    <div className="px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-border/20">
      <div className="glass-card rounded-2xl overflow-hidden border border-border/50 flex">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'overview' 
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Layers className={cn("w-4 h-4", activeTab === 'overview' && "text-primary")} />
          {t('shipments.overview')}
          {activeTab === 'overview' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('order')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'order' 
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <ShoppingCart className={cn("w-4 h-4", activeTab === 'order' && "text-primary")} />
          {t('shipments.order')}
          {activeTab === 'order' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('weighbridge')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'weighbridge'
              ? "bg-primary/15 text-primary font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Gauge className={cn("w-4 h-4", activeTab === 'weighbridge' && "text-primary")} />
          {t('shipments.weighbridge')}
          {activeTab === 'weighbridge' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={config.gradient}
      headerContent={headerContent}
      topBarContent={topBarContent}
      stickyContent={tabsContent}
      footerContent={footerContent}
      fixedWidth={600}
      title={t('shipments.shipmentDetails')}
    >
      {/* Tab Content - scrollable */}
      {activeTab === 'overview' && (
              <>
                {/* Product Info Card */}
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {isArabic ? shipment.product.ar : shipment.product.en}
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('shipments.productType')}</p>
                      <p className="text-sm font-semibold text-foreground">Bulk Cement</p>
                    </div>
                  </div>
                </div>

                {/* Route Card */}
                <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                  <h3 className="text-sm font-semibold text-foreground mb-4">{t('shipments.routeInfo')}</h3>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.from')}</p>
                        <p className="text-sm font-semibold text-foreground">{shipment.origin}</p>
                      </div>
                    </div>

                    <div className={cn("pl-5", isRTL && "pr-5 pl-0")}>
                      <ArrowDown className={cn("w-4 h-4 text-blue-400", isRTL && "rotate-180")} />
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.to')}</p>
                        <p className="text-sm font-semibold text-foreground">{shipment.destination}</p>
                      </div>
                    </div>

                    {shipment.eta && (
                      <div className="flex items-start gap-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                        <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shrink-0">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('shipments.eta')}</p>
                          <p className="text-lg font-bold text-blue-600">{shipment.eta}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Schedule Card */}
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('shipments.scheduledDate')}</p>
                      <p className="text-sm font-semibold text-foreground">{shipment.scheduledDate}</p>
                    </div>
                  </div>
                </div>

                {/* Transport Card */}
                {(shipment.driverName || shipment.truckNumber) && (
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-foreground mb-4">{t('shipments.transport')}</h3>

                    <div className="space-y-4">
                      {shipment.driverName && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shrink-0">
                            <User className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-muted-foreground mb-1">{t('shipments.driver')}</p>
                            <p className="text-sm font-semibold text-foreground">{shipment.driverName}</p>
                          </div>
                          <Button size="icon" variant="ghost" className={cn("rounded-full bg-white dark:bg-slate-700 text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-600 shrink-0", isRTL && "order-first")}>
                            <Phone className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {shipment.truckNumber && (
                        <div className={cn(`flex items-start gap-3 ${shipment.driverName ? 'pt-3 border-t border-slate-200 dark:border-slate-700' : ''}`)}>
                          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shrink-0">
                            <Truck className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t('shipments.truck')}</p>
                            <p className="text-sm font-semibold text-foreground font-mono">{shipment.truckNumber}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'weighbridge' && (
              <>
                {weighbridgeLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-4">
                      <Gauge className="w-4 h-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold text-foreground">{t('shipments.weighbridge')}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeGross')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeWeight(weighbridge?.grossWeight)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeTare')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeWeight(weighbridge?.tareWeight)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeNet')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeWeight(weighbridge?.netWeight)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeTicket')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeText(weighbridge?.ticketNumber)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeMeasuredAt')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeDate(weighbridge?.measuredAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('shipments.weighbridgeScale')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatWeighbridgeText(weighbridge?.scaleId)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'order' && (
              <>
                {orderLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : order ? (
                  <OrderCard
                    order={order}
                    onClick={onViewOrder ? () => onViewOrder(order.id) : undefined}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <ShoppingCart className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{t('orders.notFound')}</p>
                    <p className="text-xs text-muted-foreground">{t('orders.orderNotFoundDescription')}</p>
                  </div>
                )}
              </>
            )}
    </BaseDrawer>
  )
}
